import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { supervisionService, employeeService, patientService } from '../services/api';
import { supervisionRulesApi } from '../services/supervisionRulesApi';
import { supervisionSocketManager } from '../services/SupervisionSocketManager';
import { Supervision, Employee, Patient } from '../types';
import { SupervisionFrequencyRule } from '../types/supervisionRules';
import { useAuth } from '../hooks/useAuth';

// Types for the context
interface SupervisionState {
  supervisions: Supervision[];
  employees: Employee[];
  patients: Patient[];
  supervisionRules: SupervisionFrequencyRule[];
  dashboardMetrics: any;
  loading: {
    supervisions: boolean;
    employees: boolean;
    patients: boolean;
    rules: boolean;
    metrics: boolean;
  };
  errors: {
    supervisions: string | null;
    employees: string | null;
    patients: string | null;
    rules: string | null;
    metrics: string | null;
  };
  lastUpdated: {
    supervisions: Date | null;
    employees: Date | null;
    patients: Date | null;
    rules: Date | null;
    metrics: Date | null;
  };
  realTimeConnection: {
    isConnected: boolean;
    connectionType: 'socket' | 'polling' | 'offline';
    lastHeartbeat: Date | null;
  };
  filters: {
    dateRange?: { start: Date; end: Date };
    supervisionType?: string;
    employeeId?: string;
    patientId?: string;
    status?: string;
  };
  selectedSupervision: Supervision | null;
  bulkOperations: {
    selectedIds: string[];
    isProcessing: boolean;
    lastOperation: { type: string; result?: any; error?: string } | null;
  };
}

// Action types
type SupervisionAction =
  | { type: 'SET_LOADING'; payload: { key: keyof SupervisionState['loading']; value: boolean } }
  | { type: 'SET_ERROR'; payload: { key: keyof SupervisionState['errors']; value: string | null } }
  | { type: 'SET_SUPERVISIONS'; payload: Supervision[] }
  | { type: 'SET_EMPLOYEES'; payload: Employee[] }
  | { type: 'SET_PATIENTS'; payload: Patient[] }
  | { type: 'SET_SUPERVISION_RULES'; payload: SupervisionFrequencyRule[] }
  | { type: 'SET_DASHBOARD_METRICS'; payload: any }
  | { type: 'UPDATE_SUPERVISION'; payload: Supervision }
  | { type: 'ADD_SUPERVISION'; payload: Supervision }
  | { type: 'DELETE_SUPERVISION'; payload: string }
  | { type: 'SET_REAL_TIME_CONNECTION'; payload: Partial<SupervisionState['realTimeConnection']> }
  | { type: 'SET_FILTERS'; payload: Partial<SupervisionState['filters']> }
  | { type: 'SET_SELECTED_SUPERVISION'; payload: Supervision | null }
  | { type: 'SET_BULK_OPERATION'; payload: Partial<SupervisionState['bulkOperations']> }
  | { type: 'CLEAR_ERRORS' }
  | { type: 'REFRESH_ALL_DATA' };

// Initial state
const initialState: SupervisionState = {
  supervisions: [],
  employees: [],
  patients: [],
  supervisionRules: [],
  dashboardMetrics: null,
  loading: {
    supervisions: false,
    employees: false,
    patients: false,
    rules: false,
    metrics: false,
  },
  errors: {
    supervisions: null,
    employees: null,
    patients: null,
    rules: null,
    metrics: null,
  },
  lastUpdated: {
    supervisions: null,
    employees: null,
    patients: null,
    rules: null,
    metrics: null,
  },
  realTimeConnection: {
    isConnected: false,
    connectionType: 'offline',
    lastHeartbeat: null,
  },
  filters: {},
  selectedSupervision: null,
  bulkOperations: {
    selectedIds: [],
    isProcessing: false,
    lastOperation: null,
  },
};

// Reducer function
function supervisionReducer(state: SupervisionState, action: SupervisionAction): SupervisionState {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        loading: { ...state.loading, [action.payload.key]: action.payload.value },
      };

    case 'SET_ERROR':
      return {
        ...state,
        errors: { ...state.errors, [action.payload.key]: action.payload.value },
      };

    case 'SET_SUPERVISIONS':
      return {
        ...state,
        supervisions: action.payload,
        lastUpdated: { ...state.lastUpdated, supervisions: new Date() },
        errors: { ...state.errors, supervisions: null },
      };

    case 'SET_EMPLOYEES':
      return {
        ...state,
        employees: action.payload,
        lastUpdated: { ...state.lastUpdated, employees: new Date() },
        errors: { ...state.errors, employees: null },
      };

    case 'SET_PATIENTS':
      return {
        ...state,
        patients: action.payload,
        lastUpdated: { ...state.lastUpdated, patients: new Date() },
        errors: { ...state.errors, patients: null },
      };

    case 'SET_SUPERVISION_RULES':
      return {
        ...state,
        supervisionRules: action.payload,
        lastUpdated: { ...state.lastUpdated, rules: new Date() },
        errors: { ...state.errors, rules: null },
      };

    case 'SET_DASHBOARD_METRICS':
      return {
        ...state,
        dashboardMetrics: action.payload,
        lastUpdated: { ...state.lastUpdated, metrics: new Date() },
        errors: { ...state.errors, metrics: null },
      };

    case 'UPDATE_SUPERVISION':
      return {
        ...state,
        supervisions: state.supervisions.map((s) =>
          s.id === action.payload.id ? { ...s, ...action.payload } : s
        ),
        lastUpdated: { ...state.lastUpdated, supervisions: new Date() },
      };

    case 'ADD_SUPERVISION':
      return {
        ...state,
        supervisions: [...state.supervisions, action.payload],
        lastUpdated: { ...state.lastUpdated, supervisions: new Date() },
      };

    case 'DELETE_SUPERVISION':
      return {
        ...state,
        supervisions: state.supervisions.filter((s) => s.id !== action.payload),
        lastUpdated: { ...state.lastUpdated, supervisions: new Date() },
      };

    case 'SET_REAL_TIME_CONNECTION':
      return {
        ...state,
        realTimeConnection: { ...state.realTimeConnection, ...action.payload },
      };

    case 'SET_FILTERS':
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
      };

    case 'SET_SELECTED_SUPERVISION':
      return {
        ...state,
        selectedSupervision: action.payload,
      };

    case 'SET_BULK_OPERATION':
      return {
        ...state,
        bulkOperations: { ...state.bulkOperations, ...action.payload },
      };

    case 'CLEAR_ERRORS':
      return {
        ...state,
        errors: {
          supervisions: null,
          employees: null,
          patients: null,
          rules: null,
          metrics: null,
        },
      };

    case 'REFRESH_ALL_DATA':
      return {
        ...state,
        loading: {
          supervisions: true,
          employees: true,
          patients: true,
          rules: true,
          metrics: true,
        },
      };

    default:
      return state;
  }
}

// Context interface
interface SupervisionContextType {
  state: SupervisionState;
  dispatch: React.Dispatch<SupervisionAction>;
  // Data fetching functions
  fetchSupervisions: () => Promise<void>;
  fetchEmployees: () => Promise<void>;
  fetchPatients: () => Promise<void>;
  fetchSupervisionRules: () => Promise<void>;
  fetchDashboardMetrics: () => Promise<void>;
  // CRUD operations
  createSupervision: (supervision: Partial<Supervision>) => Promise<Supervision>;
  updateSupervision: (id: string, updates: Partial<Supervision>) => Promise<Supervision>;
  deleteSupervision: (id: string) => Promise<void>;
  // Bulk operations
  bulkUpdateSupervisions: (updates: Array<{ id: string; [key: string]: any }>) => Promise<void>;
  exportSupervisions: (params?: any) => Promise<void>;
  // Filtering and selection
  setFilters: (filters: Partial<SupervisionState['filters']>) => void;
  setSelectedSupervision: (supervision: Supervision | null) => void;
  // Utility functions
  getFilteredSupervisions: () => Supervision[];
  getSupervisionById: (id: string) => Supervision | undefined;
  getEmployeeById: (id: string) => Employee | undefined;
  getPatientById: (id: string) => Patient | undefined;
  // Real-time functions
  subscribeToUpdates: () => Promise<void>;
  unsubscribeFromUpdates: () => void;
  refreshAllData: () => Promise<void>;
  // Permission checking
  hasPermission: (action: string, resource?: any) => boolean;
}

// Create context
const SupervisionContext = createContext<SupervisionContextType | undefined>(undefined);

// Provider component
export const SupervisionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(supervisionReducer, initialState);
  const { user, loading } = useAuth();
  
  // Check if user is authenticated (user exists and not loading)
  const isAuthenticated = !!user && !loading;

  // Data fetching functions
  const fetchSupervisions = async () => {
    dispatch({ type: 'SET_LOADING', payload: { key: 'supervisions', value: true } });
    try {
      const supervisions = await supervisionService.getSupervisions();
      dispatch({ type: 'SET_SUPERVISIONS', payload: supervisions });
    } catch (error) {
      dispatch({
        type: 'SET_ERROR',
        payload: {
          key: 'supervisions',
          value: error instanceof Error ? error.message : 'Failed to fetch supervisions',
        },
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { key: 'supervisions', value: false } });
    }
  };

  const fetchEmployees = async () => {
    dispatch({ type: 'SET_LOADING', payload: { key: 'employees', value: true } });
    try {
      const employees = await employeeService.getAllEmployees();
      dispatch({ type: 'SET_EMPLOYEES', payload: employees });
    } catch (error) {
      dispatch({
        type: 'SET_ERROR',
        payload: {
          key: 'employees',
          value: error instanceof Error ? error.message : 'Failed to fetch employees',
        },
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { key: 'employees', value: false } });
    }
  };

  const fetchPatients = async () => {
    dispatch({ type: 'SET_LOADING', payload: { key: 'patients', value: true } });
    try {
      const patients = await patientService.getAllPatients();
      dispatch({ type: 'SET_PATIENTS', payload: patients });
    } catch (error) {
      dispatch({
        type: 'SET_ERROR',
        payload: {
          key: 'patients',
          value: error instanceof Error ? error.message : 'Failed to fetch patients',
        },
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { key: 'patients', value: false } });
    }
  };

  const fetchSupervisionRules = async () => {
    dispatch({ type: 'SET_LOADING', payload: { key: 'rules', value: true } });
    try {
      const response = await supervisionRulesApi.getAll({ includeInactive: false });
      dispatch({ type: 'SET_SUPERVISION_RULES', payload: response.rules });
    } catch (error) {
      dispatch({
        type: 'SET_ERROR',
        payload: {
          key: 'rules',
          value: error instanceof Error ? error.message : 'Failed to fetch supervision rules',
        },
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { key: 'rules', value: false } });
    }
  };

  const fetchDashboardMetrics = async () => {
    dispatch({ type: 'SET_LOADING', payload: { key: 'metrics', value: true } });
    try {
      const metrics = await supervisionService.getDashboardMetrics();
      dispatch({ type: 'SET_DASHBOARD_METRICS', payload: metrics });
    } catch (error) {
      dispatch({
        type: 'SET_ERROR',
        payload: {
          key: 'metrics',
          value: error instanceof Error ? error.message : 'Failed to fetch dashboard metrics',
        },
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { key: 'metrics', value: false } });
    }
  };

  // CRUD operations
  const createSupervision = async (supervision: Partial<Supervision>): Promise<Supervision> => {
    const newSupervision = await supervisionService.createSupervision(supervision);
    dispatch({ type: 'ADD_SUPERVISION', payload: newSupervision });
    return newSupervision;
  };

  const updateSupervision = async (id: string, updates: Partial<Supervision>): Promise<Supervision> => {
    const updatedSupervision = await supervisionService.updateSupervision(id, updates);
    dispatch({ type: 'UPDATE_SUPERVISION', payload: updatedSupervision });
    return updatedSupervision;
  };

  const deleteSupervision = async (id: string): Promise<void> => {
    await supervisionService.deleteSupervision(id);
    dispatch({ type: 'DELETE_SUPERVISION', payload: id });
  };

  // Bulk operations
  const bulkUpdateSupervisions = async (updates: Array<{ id: string; [key: string]: any }>): Promise<void> => {
    dispatch({ type: 'SET_BULK_OPERATION', payload: { isProcessing: true } });
    try {
      const result = await supervisionService.bulkUpdateSupervisions(updates);
      dispatch({
        type: 'SET_BULK_OPERATION',
        payload: {
          isProcessing: false,
          lastOperation: { type: 'bulk_update', result },
        },
      });
      // Refresh supervisions after bulk update
      await fetchSupervisions();
    } catch (error) {
      dispatch({
        type: 'SET_BULK_OPERATION',
        payload: {
          isProcessing: false,
          lastOperation: {
            type: 'bulk_update',
            error: error instanceof Error ? error.message : 'Bulk update failed',
          },
        },
      });
      throw error;
    }
  };

  const exportSupervisions = async (params?: any): Promise<void> => {
    await supervisionService.exportSupervisions(params);
  };

  // Filtering and selection
  const setFilters = (filters: Partial<SupervisionState['filters']>) => {
    dispatch({ type: 'SET_FILTERS', payload: filters });
  };

  const setSelectedSupervision = (supervision: Supervision | null) => {
    dispatch({ type: 'SET_SELECTED_SUPERVISION', payload: supervision });
  };

  // Utility functions
  const getFilteredSupervisions = (): Supervision[] => {
    let filtered = [...state.supervisions];

    if (state.filters.dateRange) {
      filtered = filtered.filter((s) => {
        const supervisionDate = new Date(s.supervisionDate);
        return (
          supervisionDate >= state.filters.dateRange!.start &&
          supervisionDate <= state.filters.dateRange!.end
        );
      });
    }

    if (state.filters.supervisionType && state.filters.supervisionType !== 'ALL') {
      filtered = filtered.filter((s) => s.supervisionType === state.filters.supervisionType);
    }

    if (state.filters.employeeId) {
      filtered = filtered.filter((s) => s.employeeId === state.filters.employeeId);
    }

    if (state.filters.patientId) {
      filtered = filtered.filter((s) => s.patientId === state.filters.patientId);
    }

    return filtered;
  };

  const getSupervisionById = (id: string): Supervision | undefined => {
    return state.supervisions.find((s) => s.id === id);
  };

  const getEmployeeById = (id: string): Employee | undefined => {
    return state.employees.find((e) => e.id === id);
  };

  const getPatientById = (id: string): Patient | undefined => {
    return state.patients.find((p) => p.id === id);
  };

  // Real-time functions
  const subscribeToUpdates = async () => {
    // Set up event listeners
    supervisionSocketManager.on('connection_state_change', (connectionState: any) => {
      dispatch({ type: 'SET_REAL_TIME_CONNECTION', payload: connectionState });
    });

    supervisionSocketManager.on('supervision_update', (update: any) => {
      if (update.type === 'STATUS_CHANGE' || update.type === 'COMPLETION') {
        // Refresh data when updates are received
        fetchSupervisions();
        fetchDashboardMetrics();
      }
    });

    // Start the SupervisionSocketManager only after authentication
    try {
      await supervisionSocketManager.start();
    } catch (error) {
      console.error('Failed to start SupervisionSocketManager:', error);
    }
  };

  const unsubscribeFromUpdates = () => {
    supervisionSocketManager.disconnect();
  };

  const refreshAllData = async () => {
    dispatch({ type: 'REFRESH_ALL_DATA' });
    await Promise.all([
      fetchSupervisions(),
      fetchEmployees(),
      fetchPatients(),
      fetchSupervisionRules(),
      fetchDashboardMetrics(),
    ]);
  };

  // Permission checking based on user role and JWT
  const hasPermission = (action: string, resource?: any): boolean => {
    if (!user) return false;

    // Super admin has all permissions
    if (user.isSuperAdmin) return true;

    // Define role-based permissions
    const permissions: Record<string, string[]> = {
      ADMIN: [
        'create_supervision',
        'update_supervision',
        'delete_supervision',
        'view_supervision',
        'export_data',
        'view_analytics',
      ],
      SUPERVISOR: [
        'create_supervision',
        'update_supervision',
        'view_supervision',
        'export_data',
        'view_analytics',
      ],
      USER: ['view_supervision'],
    };

    const userRole = user.role || 'USER';
    const rolePermissions = permissions[userRole] || [];

    return rolePermissions.includes(action);
  };

  // Initialize data when component mounts (only after authentication)
  useEffect(() => {
    let isMounted = true;

    const initializeSupervisionData = async () => {
      if (isAuthenticated && user && isMounted) {
        console.log('User authenticated, initializing supervision data...');
        await refreshAllData();
        await subscribeToUpdates();
      }
    };

    initializeSupervisionData();

    return () => {
      isMounted = false;
      unsubscribeFromUpdates();
    };
  }, [isAuthenticated, user]);

  const contextValue: SupervisionContextType = {
    state,
    dispatch,
    fetchSupervisions,
    fetchEmployees,
    fetchPatients,
    fetchSupervisionRules,
    fetchDashboardMetrics,
    createSupervision,
    updateSupervision,
    deleteSupervision,
    bulkUpdateSupervisions,
    exportSupervisions,
    setFilters,
    setSelectedSupervision,
    getFilteredSupervisions,
    getSupervisionById,
    getEmployeeById,
    getPatientById,
    subscribeToUpdates,
    unsubscribeFromUpdates,
    refreshAllData,
    hasPermission,
  };

  return <SupervisionContext.Provider value={contextValue}>{children}</SupervisionContext.Provider>;
};

// Custom hook to use the context
export const useSupervision = (): SupervisionContextType => {
  const context = useContext(SupervisionContext);
  if (context === undefined) {
    throw new Error('useSupervision must be used within a SupervisionProvider');
  }
  return context;
};

export default SupervisionContext;
